import { useEffect, useRef, useState } from "react";
import {
  bootMapKit,
  reverseGeocode,
  searchMapKit,
  type MapKitHit,
} from "../mapkit";

type PickerMode = "search" | "pin";

/**
 * Modal Apple Maps place picker for the curator catalog manager. Two
 * modes:
 *   - **Search** — MapKit JS `Search` results, picked by tap.
 *   - **Drop pin** — interactive MapKit JS map with a draggable
 *     annotation. Tap the map to move the pin, drag it to refine.
 *     Apple reverse-geocodes the final coord so city + state
 *     backfill automatically; if it can't, we still capture lat/lng
 *     and apply them.
 *
 * The pin mode is the fallback for trails Apple Maps doesn't index
 * (private mountain bike systems, unmarked trailheads, etc.).
 */
export function MapKitPlacePicker({
  initialQuery,
  nearCity,
  nearState,
  initialLatitude,
  initialLongitude,
  onPick,
  onClose,
  embedded = false,
}: {
  initialQuery: string;
  nearCity?: string;
  nearState?: string;
  /** If the trail already has coords, drop-pin mode opens there;
   *  otherwise we centre on the city/state region. */
  initialLatitude?: number | null;
  initialLongitude?: number | null;
  onPick: (hit: MapKitHit) => void;
  /** Only used when the picker is a stand-alone modal. Optional so
   *  embedded callers (the curator drawer) can host the picker
   *  inline without an explicit close affordance. */
  onClose?: () => void;
  /** When true, render only the picker body — no modal backdrop, no
   *  card chrome, no close button — so a parent drawer/sheet can
   *  embed it as one section among many. The picker still calls
   *  onPick when the curator confirms; the parent decides what
   *  to do next (typically: stash the coords + city/state, don't
   *  close any container). */
  embedded?: boolean;
}) {
  const [mode, setMode] = useState<PickerMode>("search");
  const [query, setQuery] = useState(initialQuery);
  const [hits, setHits] = useState<MapKitHit[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [booted, setBooted] = useState(false);

  useEffect(() => {
    let cancelled = false;
    bootMapKit()
      .then(() => {
        if (!cancelled) setBooted(true);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(
            err instanceof Error
              ? `MapKit JS unavailable: ${err.message}`
              : "MapKit JS unavailable"
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (booted && initialQuery.trim() && mode === "search") {
      runSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booted]);

  async function runSearch() {
    const trimmed = query.trim();
    if (!trimmed) return;
    setLoading(true);
    setError(null);
    try {
      const results = await searchMapKit(trimmed, nearCity, nearState);
      setHits(results);
      if (results.length === 0) setError("No places matched.");
    } catch (err) {
      setHits([]);
      setError(err instanceof Error ? err.message : "Search failed.");
    } finally {
      setLoading(false);
    }
  }

  const biasLabel = [nearCity, nearState]
    .filter((p) => p && p.trim().length > 0)
    .join(", ");

  // Shared body — segmented control + mode panes. Used by both the
  // modal (with onPick→onClose) and the embedded variant (just onPick;
  // parent decides what to do next).
  const body = (
    <>
      {/* Mode segmented control — Search vs Drop pin. Apple-HIG
          style segmented buttons. */}
      <div className="row" style={{ gap: 0, marginBottom: 12 }}>
        <button
          type="button"
          className={mode === "search" ? "" : "secondary"}
          style={{
            flex: 1,
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0,
          }}
          onClick={() => setMode("search")}
        >
          🔎 Search
        </button>
        <button
          type="button"
          className={mode === "pin" ? "" : "secondary"}
          style={{
            flex: 1,
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0,
          }}
          onClick={() => setMode("pin")}
        >
          📍 Drop pin
        </button>
      </div>

      {!booted && !error && (
        <div className="muted" style={{ fontSize: 13 }}>
          Loading MapKit…
        </div>
      )}

      {error && (
        <div className="error" style={{ fontSize: 13 }}>
          {error}
        </div>
      )}

      {mode === "search" && (
        <SearchPane
          query={query}
          setQuery={setQuery}
          onSearch={runSearch}
          loading={loading}
          booted={booted}
          biasLabel={biasLabel}
          hits={hits}
          onPick={(hit) => {
            onPick(hit);
            onClose?.();
          }}
        />
      )}

      {mode === "pin" && booted && (
        <DropPinPane
          initialLatitude={initialLatitude ?? null}
          initialLongitude={initialLongitude ?? null}
          biasCity={nearCity ?? ""}
          biasState={nearState ?? ""}
          onConfirm={(hit) => {
            onPick(hit);
            onClose?.();
          }}
        />
      )}
    </>
  );

  if (embedded) {
    return <div className="mapkit-embedded">{body}</div>;
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal-card"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 640 }}
      >
        <div className="modal-head">
          <h3 style={{ margin: 0 }}>Find on Apple Maps</h3>
          <button
            className="secondary small"
            onClick={onClose}
            aria-label="Close"
          >
            Close
          </button>
        </div>

        <div className="modal-body">{body}</div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Search pane
// ---------------------------------------------------------------------------

function SearchPane({
  query,
  setQuery,
  onSearch,
  loading,
  booted,
  biasLabel,
  hits,
  onPick,
}: {
  query: string;
  setQuery: (s: string) => void;
  onSearch: () => void;
  loading: boolean;
  booted: boolean;
  biasLabel: string;
  hits: MapKitHit[];
  onPick: (hit: MapKitHit) => void;
}) {
  return (
    <>
      <div className="row" style={{ gap: 8 }}>
        <input
          type="text"
          autoFocus
          placeholder="Trail or place name"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") onSearch();
          }}
          style={{ flex: 1 }}
        />
        <button onClick={onSearch} disabled={!booted || loading}>
          {loading ? "Searching…" : "Search"}
        </button>
      </div>

      {biasLabel && (
        <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
          Biasing to {biasLabel}
        </div>
      )}

      {hits.length > 0 && (
        <ul
          style={{
            listStyle: "none",
            margin: "12px 0 0 0",
            padding: 0,
            maxHeight: 360,
            overflowY: "auto",
          }}
        >
          {hits.map((hit, i) => (
            <li key={`${hit.name}-${i}`}>
              <button
                type="button"
                onClick={() => onPick(hit)}
                style={{
                  display: "block",
                  width: "100%",
                  textAlign: "left",
                  background: "transparent",
                  border: 0,
                  borderTop: "1px solid var(--border)",
                  padding: "10px 4px",
                  cursor: "pointer",
                }}
              >
                <div style={{ fontWeight: 500 }}>{hit.name}</div>
                <div className="muted" style={{ fontSize: 12 }}>
                  {hit.subtitle} · {hit.latitude.toFixed(4)},{" "}
                  {hit.longitude.toFixed(4)}
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Drop-pin pane
// ---------------------------------------------------------------------------

function DropPinPane({
  initialLatitude,
  initialLongitude,
  biasCity,
  biasState,
  onConfirm,
}: {
  initialLatitude: number | null;
  initialLongitude: number | null;
  biasCity: string;
  biasState: string;
  onConfirm: (hit: MapKitHit) => void;
}) {
  const mapHostRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<any>(null);
  const annotationRef = useRef<any>(null);
  const [lat, setLat] = useState<number | null>(initialLatitude);
  const [lng, setLng] = useState<number | null>(initialLongitude);
  const [resolving, setResolving] = useState(false);

  // Boot a MapKit JS map into the host div once. The annotation is
  // created at the initial coord (existing trail coords if present,
  // city/state centroid as a fallback, US centre as a last resort).
  useEffect(() => {
    if (!mapHostRef.current || !window.mapkit) return;

    const start = pickStartCoord(initialLatitude, initialLongitude);
    setLat(start.latitude);
    setLng(start.longitude);

    const map = new window.mapkit.Map(mapHostRef.current, {
      colorScheme: window.mapkit.Map.ColorSchemes.Dark,
      mapType: window.mapkit.Map.MapTypes.Standard,
      showsZoomControl: true,
      isRotationEnabled: false,
    });
    map.region = new window.mapkit.CoordinateRegion(
      new window.mapkit.Coordinate(start.latitude, start.longitude),
      new window.mapkit.CoordinateSpan(start.span, start.span)
    );

    const annotation = new window.mapkit.MarkerAnnotation(
      new window.mapkit.Coordinate(start.latitude, start.longitude),
      {
        draggable: true,
        color: "#2EA56A",
        title: "Trailhead",
        subtitle: "Drag to refine",
      }
    );
    annotation.addEventListener("drag-end", () => {
      const c = annotation.coordinate;
      setLat(c.latitude);
      setLng(c.longitude);
    });
    map.addAnnotation(annotation);
    annotationRef.current = annotation;

    // Tap anywhere on the map → move the pin to that location. Apple's
    // event payload carries the tap coordinate directly.
    map.addEventListener("single-tap", (event: any) => {
      // event.pointOnPage is screen coords; convert to map coords.
      const pt = event.pointOnPage;
      const coord = map.convertPointOnPageToCoordinate(pt);
      annotation.coordinate = coord;
      setLat(coord.latitude);
      setLng(coord.longitude);
    });

    mapRef.current = map;

    // If we don't have a precise initial coord, try biasing the camera
    // via a low-precision geocode of the city/state — gives a useful
    // starting point even before the user drags.
    if (initialLatitude == null && (biasCity || biasState)) {
      biasCameraToCityState(map, biasCity, biasState).catch(() => undefined);
    }

    return () => {
      map.destroy();
      mapRef.current = null;
      annotationRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleConfirm() {
    if (lat == null || lng == null) return;
    setResolving(true);
    try {
      // Reverse geocode for a nicer subtitle + city/state backfill.
      // If Apple doesn't know the area we still apply the raw coords.
      const hit = await reverseGeocode(lat, lng);
      if (hit) {
        onConfirm(hit);
      } else {
        onConfirm({
          name: "Manual pin",
          subtitle: `${lat.toFixed(5)}, ${lng.toFixed(5)}`,
          latitude: lat,
          longitude: lng,
          stateCode: null,
          city: null,
        });
      }
    } finally {
      setResolving(false);
    }
  }

  return (
    <div>
      <div
        ref={mapHostRef}
        style={{
          width: "100%",
          height: 380,
          borderRadius: 12,
          overflow: "hidden",
          border: "1px solid var(--border)",
        }}
      />
      <div
        className="muted"
        style={{
          fontSize: 12,
          marginTop: 8,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          flexWrap: "wrap",
        }}
      >
        <span>
          Tap the map to drop a pin, or drag the existing one. Apple Maps
          will reverse-geocode for city + state when you save.
        </span>
        <code>
          {lat != null && lng != null
            ? `${lat.toFixed(5)}, ${lng.toFixed(5)}`
            : "—"}
        </code>
      </div>
      <div className="row" style={{ justifyContent: "flex-end", marginTop: 10, gap: 8 }}>
        <button onClick={handleConfirm} disabled={lat == null || lng == null || resolving}>
          {resolving ? "Saving…" : "Use this location"}
        </button>
      </div>
    </div>
  );
}

// Centre + span heuristics for the drop-pin map. A precise existing
// coord gets a tight 0.05° span; a city-level start gets ~0.4° so the
// curator can see the whole city; nothing → continental US.
function pickStartCoord(
  lat: number | null,
  lng: number | null
): { latitude: number; longitude: number; span: number } {
  if (lat != null && lng != null) {
    return { latitude: lat, longitude: lng, span: 0.05 };
  }
  // Default fallback — continental US centre, wide span.
  return { latitude: 39.5, longitude: -98.35, span: 30 };
}

async function biasCameraToCityState(
  map: any,
  city: string,
  state: string
) {
  if (!window.mapkit) return;
  const composed = [city, state, "US"].filter((p) => p && p.trim()).join(", ");
  if (!composed) return;
  const search = new window.mapkit.Search({ getsUserLocation: false });
  return new Promise<void>((resolve) => {
    search.search(composed, (err: unknown, data: any) => {
      if (err || !data?.places?.length) {
        resolve();
        return;
      }
      const coord = data.places[0].coordinate;
      map.region = new window.mapkit.CoordinateRegion(
        new window.mapkit.Coordinate(coord.latitude, coord.longitude),
        new window.mapkit.CoordinateSpan(0.4, 0.4)
      );
      resolve();
    });
  });
}
